import {
  extendObservable, observable, computed, autorun,
} from 'mobx';
import axios from '../../../libs/axios_csrf';
import {
  MEETINGS_STATE,
  MEETING_POINT_STATUSES_INTEGER,
} from '../../igmeetings_components/constants.js';

// First page of meetings pagination
const INITIAL_PAGE = 1;

class MyMeetingsStore {
  constructor() {
    const nextPage = {};
    const totalEntries = {};
    const meetingsStates = this.getMeetingsStates();
    for (const state of meetingsStates) {
      nextPage[state] = INITIAL_PAGE;
      totalEntries[state] = 0;
    }
    extendObservable(this, {
      meetings: [],
      meetingsIds: [],
      currentUser: null,
      routes: {},
      nextPage,
      totalEntries,
      loading: false,
    });

    this.fetchMeetingsIndex();
  }

  isValidMeetingsState(state) {
    return state == MEETINGS_STATE.CONFIRMED || state == MEETINGS_STATE.PENDING;
  }

  loadMoreMeetingsByState(state, nextPage) {
    if (this.isValidMeetingsState(state)) {
      this.fetchMeetingsByState(
        state,
        nextPage == undefined ? this.nextPage[state] : nextPage,
      );
    }
  }

  hasMoreItems(state) {
    if (this.isValidMeetingsState(state)) {
      return this.nextPage[state] != null;
    }
    return false;
  }

  getMeetingsCountByState(state) {
    return this.totalEntries[state];
  }

  // Get the first page of all meeting states
  fetchMeetingsIndex() {
    const meetingsStates = this.getMeetingsStates();
    this.loading = true;
    for (const state of meetingsStates) {
      this.fetchMeetingsByState(state, this.nextPage[state]);
    }
  }

  // Fetch the next page of meetings of a given type
  fetchMeetingsByState(state, nextPage) {
    if (nextPage) {
      axios
        .get('/my_meetings/', {
          params: {
            state,
            page: nextPage,
          },
        })
        .then((response) => {
          const meetingsList = response.data.meetings;
          this.meetings = this.meetings.concat(
            meetingsList.filter((meeting) => {
              if (this.meetingsIds.includes(meeting.id)) return false;

              this.meetingsIds.push(meeting.id);
              return true;
            }),
          );

          this.nextPage[state] = response.data.next_page;
          this.totalEntries[state] = response.data.total_entries;
          this.loading = false;
        })
        .catch((thrown) => {
          if (axios.isCancel(thrown)) {
            console.log('Request canceled', thrown.message);
          } else {
            console.log(thrown);
          }
        });
    }
  }

  getMeetingsStates() {
    return [MEETINGS_STATE.PENDING, MEETINGS_STATE.CONFIRMED];
  }

  getAllMeetings() {
    return this.meetings;
  }

  getMeetingById(id) {
    id = parseInt(id);
    const meeting = this.meetings.find(el => el.id == id);
    return meeting;
  }

  getMeetingsByState(state) {
    const meetings = this.meetings.filter(
      el => this.getParticipation(el).state == state,
    );
    return meetings;
  }

  setCurrentUser(currentUser) {
    this.currentUser = currentUser;
  }

  getRoutes() {
    return this.routes;
  }

  setRoutes(controller) {
    const routes = {
      show: '/my_meetings/:id',
      index: '/my_meetings/list/:state',
      pending: '/my_meetings/list/pending',
      confirmed: '/my_meetings/list/confirmed',
      itemPathPrefix: '/my_meetings/',
      redirect: '/my_meetings/list/pending',
    };

    if (controller != undefined) {
      routes.show = `/${controller}${routes.show}`;
      routes.index = `/${controller}${routes.index}`;
      routes.pending = `/${controller}${routes.pending}`;
      routes.confirmed = `/${controller}${routes.confirmed}`;
      routes.itemPathPrefix = `/${controller}${routes.itemPathPrefix}`;
      routes.redirect = `/${controller}${routes.redirect}`;
    }

    this.routes = routes;
  }

  getParticipation(meeting) {
    return meeting.participations.find(el => el.user.id == this.currentUser.id);
  }

  confirmMeetingInvitation(meeting) {
    const participation = this.getParticipation(meeting);
    participation.state = 'confirmed';
    this.totalEntries[MEETINGS_STATE.CONFIRMED] += 1;
    this.totalEntries[MEETINGS_STATE.PENDING] -= 1;
    this.patchParticipation(participation);
  }

  declineMeetingInvitation(meeting) {
    const participation = this.getParticipation(meeting);
    participation.state = 'declined';
    this.totalEntries[MEETINGS_STATE.PENDING] -= 1;
    this.patchParticipation(participation);
  }

  patchParticipation(participation) {
    axios
      .patch(`/my_meetings/${participation.id}.json`, {
        participation: {
          state: participation.state,
        },
      })
      .then((response) => {})
      .catch((errors) => {
        console.log(errors);
      });
  }

  updateMeetingPointStatus(meetingPointId, status) {
    axios
      .patch(`/meeting_points/${meetingPointId}.json`, {
        id: meetingPointId,
        status,
      })
      .then((response) => {
        const { meeting_id } = response.data;

        const index = this.getMeetingById(meeting_id).meeting_points.findIndex(
          el => el.id == meetingPointId,
        );

        this.getMeetingById(meeting_id).meeting_points[
          index
        ].status = Object.keys(MEETING_POINT_STATUSES_INTEGER).find(
          key => MEETING_POINT_STATUSES_INTEGER[key] === status,
        );
      })
      .catch(error => console.log(error));
  }
}

export default new MyMeetingsStore();
